import { useEffect, useMemo, useState } from 'react';
import { Check } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { ItemsTable } from '@/components/form/ItemsTable';
import { useToast } from '@/components/ui/useToast';
import { getRfq, submitManualQuote, type Rfq } from '@/api/rfq';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';

interface Props {
  open: boolean;
  rfq: Rfq | null;
  onClose: () => void;
}

interface QuoteLine {
  rfqItemId: number;
  itemName: string;
  qty: string;
  unit: string;
  unitPrice: string;
  deliveryDays: string;
  remarks: string;
}

export function QuoteEntryModal({ open, rfq, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'vendorId'>();
  const [vendorId, setVendorId] = useState<number | ''>('');
  const [validUntil, setValidUntil] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([]);

  // Fetch full RFQ detail (with existing vendor-portal quotes) so we can
  // prefill the form when the operator picks a vendor that has already
  // submitted a quote through the portal.
  const detailQuery = useQuery({
    queryKey: ['rfq-detail', rfq?.id],
    queryFn: () => getRfq(rfq!.id),
    enabled: open && !!rfq,
    staleTime: 30_000,
  });
  const rfqDetail = detailQuery.data ?? rfq;

  // Map vendorId → their submitted quote (if any). Used both to prefill
  // the form and to badge vendor options as "already quoted".
  const quoteByVendor = useMemo(() => {
    const map = new Map<number, NonNullable<Rfq['quotes']>[number]>();
    for (const q of rfqDetail?.quotes ?? []) map.set(q.vendorId, q);
    return map;
  }, [rfqDetail]);

  useEffect(() => {
    if (!open || !rfq) return;
    setVendorId('');
    setValidUntil('');
    setPaymentTerms('');
    setRemarks('');
    setLines(
      rfq.items.map((it) => ({
        rfqItemId: it.id!,
        itemName: it.itemName,
        qty: String(it.qty),
        unit: it.unit,
        unitPrice: '',
        deliveryDays: '',
        remarks: '',
      })),
    );
  }, [open, rfq]);

  // When the user picks a vendor that has ALREADY submitted a quote (via
  // the vendor portal or a prior manual entry), prefill top-level fields
  // and every line's price / delivery / remarks. Saving overwrites the
  // existing quote server-side (upsert by RFQ + vendor).
  useEffect(() => {
    if (!open || !vendorId) return;
    const existing = quoteByVendor.get(Number(vendorId));
    if (!existing) return;
    setValidUntil(existing.validUntil ? existing.validUntil.slice(0, 10) : '');
    setPaymentTerms(existing.paymentTerms ?? '');
    setRemarks(existing.remarks ?? '');
    const byRfqItem = new Map(existing.items.map((qi) => [qi.rfqItemId, qi]));
    setLines((prev) =>
      prev.map((l) => {
        const qi = byRfqItem.get(l.rfqItemId);
        return qi
          ? {
              ...l,
              unitPrice: String(qi.unitPrice),
              deliveryDays: qi.deliveryDays != null ? String(qi.deliveryDays) : '',
              remarks: qi.remarks ?? '',
            }
          : l;
      }),
    );
    toast.push('Loaded existing quote from vendor — edit and re-save if needed', 'info');
  }, [open, vendorId, quoteByVendor, toast]);

  const submitMutation = useMutation({
    mutationFn: () => {
      const payload = {
        vendorId: Number(vendorId),
        validUntil: validUntil || undefined,
        paymentTerms: paymentTerms.trim() || undefined,
        remarks: remarks.trim() || undefined,
        items: lines
          .filter((l) => Number(l.unitPrice) > 0)
          .map((l) => ({
            rfqItemId: l.rfqItemId,
            unitPrice: Number(l.unitPrice),
            deliveryDays: l.deliveryDays ? Number(l.deliveryDays) : undefined,
            remarks: l.remarks.trim() || undefined,
          })),
      };
      return submitManualQuote(rfq!.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      qc.invalidateQueries({ queryKey: ['ai-score'] });
      toast.pushStep('Quote recorded', {
        body: 'AI scoring re-run. Review results on Quote Approval.',
        href: '/purchase/quoteapproval',
        nextLabel: 'Open Quote Approval',
        tag: 'quote-recorded',
      });
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    if (!fe.validate({
      vendorId: !vendorId && 'Select a vendor',
    })) return;
    const valid = lines.filter((l) => Number(l.unitPrice) > 0);
    if (valid.length === 0) {
      toast.push('Enter at least one unit price', 'error');
      return;
    }
    submitMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Enter Quote — ${rfq?.rfqNo ?? ''}`}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? 'Saving…' : 'Record Quote'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="Vendor" required error={fe.errors.vendorId}>
          <Select
            invalid={fe.has('vendorId')}
            value={String(vendorId)}
            onChange={(e) => { setVendorId(e.target.value ? Number(e.target.value) : ''); fe.clear('vendorId'); }}
          >
            <option value="">— Select Vendor —</option>
            {(rfqDetail?.vendors ?? rfq?.vendors ?? []).map((rv) => (
              <option key={rv.vendor.id} value={rv.vendor.id}>
                {rv.vendor.name}
                {quoteByVendor.has(rv.vendor.id) ? <><Check size={11} className="ml-1 inline-block align-[-1px]" /> quoted</> : ''}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Quote Valid Until">
          <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </FormGroup>
        <FormGroup label="Payment Terms">
          <Input
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            placeholder="e.g. 30 Days Net"
          />
        </FormGroup>
        <FormGroup label="Remarks" className="col-span-2">
          <Textarea
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional notes / terms"
          />
        </FormGroup>
      </div>

      <ItemsTable<QuoteLine>
        title="Line Quotes"
        rows={lines}
        onChange={setLines}
        newRow={() => ({
          rfqItemId: 0,
          itemName: '',
          qty: '',
          unit: '',
          unitPrice: '',
          deliveryDays: '',
          remarks: '',
        })}
        columns={[
          { key: 'item', header: 'Item', cell: (row) => <span className="font-bold">{row.itemName}</span> },
          { key: 'qty', header: 'Qty', align: 'right', cell: (row) => `${row.qty} ${row.unit}` },
          {
            key: 'unitPrice',
            header: 'Unit Price (₹)',
            width: '130px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                step="0.01"
                value={row.unitPrice}
                onChange={(e) => update({ unitPrice: e.target.value })}
                className="text-right"
              />
            ),
          },
          {
            key: 'deliveryDays',
            header: 'Delivery (days)',
            width: '120px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={row.deliveryDays}
                onChange={(e) => update({ deliveryDays: e.target.value })}
                className="text-right"
              />
            ),
          },
          {
            key: 'remarks',
            header: 'Remarks',
            cell: (row, _i, update) => (
              <Input
                value={row.remarks}
                onChange={(e) => update({ remarks: e.target.value })}
                placeholder="Optional"
              />
            ),
          },
        ]}
      />
    </Modal>
  );
}
